// src/pages/vendas/PesqNotasItemPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { fetchData } from '../../services/Api';
import { useMessage } from '../../contexts/geral/MessageContext';
import { formatCurrency, formatNumeric } from '../../utils/numberUtils';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { getDescricaoTipoMov } from '../../models/estoque/TipoMov.ts';
import { getDescricaoSituacaoNF } from '../../models/vendas/SituacaoNF.ts';
import Input from '../../components/input/Input';
import InputCheck from '../../components/input/InputCheck';
import Select from '../../components/input/Select';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageFooter from '../../components/geral/PageFooter';
import PageContent from '../../components/geral/PageContent';
import { useCliente } from '../../contexts/pessoas/ClienteContext';

const PesqNotasItemPage = () => {
    const { showMessage } = useMessage();
    const { showClienteSearch } = useCliente();
    const [periodoInicial, setPeriodoInicial] = useState('');
    const [periodoFinal, setPeriodoFinal] = useState('');
    const [horaInicial, setHoraInicial] = useState('00:00');
    const [horaFinal, setHoraFinal] = useState('23:59');
    const [cliente, setCliente] = useState('');
    const [numDoc, setNumDoc] = useState('');
    const [situacao, setSituacao] = useState('-1');
    const [produtoDescricao, setProdutoDescricao] = useState('');
    const [agruparPorProduto, setAgruparPorProduto] = useState(false);
    const [itens, setItens] = useState([]);
    const [disableBtn, setDisableBtn] = useState(false);
    const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });

    const formatDateToInputValue = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const dataAtual = new Date();
        const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
        setPeriodoInicial(formatDateToInputValue(primeiroDiaMes));
        setPeriodoFinal(formatDateToInputValue(dataAtual));
    }, []);

    useEffect(() => {
        if (periodoInicial && periodoFinal) {
            buscarItens();
        }
    }, [periodoInicial, periodoFinal]);

    const buscarItens = async () => {
        try {
            setDisableBtn(true);

            const result = await fetchData('/NotaItem', {
                naturezaId: 1,
                dataLanIni: `${periodoInicial}T${horaInicial || '00:00'}:00`,
                dataLanFim: `${periodoFinal}T${horaFinal || '23:59'}:59`,
                clienteNome: cliente || null,
                numDoc: numDoc || null,
                situacaoNF: situacao || null,
                produtoDescricao: produtoDescricao || null,
            });

            setItens(result || []);
        } catch (error) {
            showMessage(`Erro ao buscar itens das notas: ${error}`, 'danger');
        } finally {
            setDisableBtn(false);
        }
    };

    const handleBuscarCliente = async () => {
        const clienteSel = await showClienteSearch();

        if (clienteSel) {
            setCliente(clienteSel.pessoa.nome);
        } else {
            setCliente('');
        }
    };

    const itensExibidos = useMemo(() => {
        const itensBase = !agruparPorProduto
            ? itens
            : Object.values(
                itens.reduce((acc, item) => {
                    const chave = String(item.produtoId || 0);

                    if (!acc[chave]) {
                        acc[chave] = {
                            ...item,
                            qtdCom: Number(item.qtdCom || 0),
                            vProd: Number(item.vProd || 0),
                        };
                        return acc;
                    }

                    acc[chave].qtdCom += Number(item.qtdCom || 0);
                    acc[chave].vProd += Number(item.vProd || 0);
                    return acc;
                }, {}),
            ).map((item) => ({
                ...item,
                vUnCom: Number(item.qtdCom || 0) > 0
                    ? Number(item.vProd || 0) / Number(item.qtdCom || 0)
                    : 0,
            }));

        const { campo, direcao } = ordenacao;

        if (!campo) {
            return itensBase;
        }

        const multiplicador = direcao === 'asc' ? 1 : -1;

        return [...itensBase].sort((a, b) => {
            const valorA = campo === 'tipoMovDescricao'
                ? getDescricaoTipoMov(a.notaTipoMov)
                : campo === 'situacaoDescricao'
                    ? getDescricaoSituacaoNF(a.notaSituacaoNF)
                    : campo === 'clienteDescricao'
                        ? `${a.notaClienteId || ''} - ${a.notaClienteNome || ''}`
                        : campo === 'hora'
                            ? formatTime(a.notaDataLanc)
                            : campo === 'produtoDescricao'
                                ? a.xProd
                                : a?.[campo];

            const valorB = campo === 'tipoMovDescricao'
                ? getDescricaoTipoMov(b.notaTipoMov)
                : campo === 'situacaoDescricao'
                    ? getDescricaoSituacaoNF(b.notaSituacaoNF)
                    : campo === 'clienteDescricao'
                        ? `${b.notaClienteId || ''} - ${b.notaClienteNome || ''}`
                        : campo === 'hora'
                            ? formatTime(b.notaDataLanc)
                            : campo === 'produtoDescricao'
                                ? b.xProd
                                : b?.[campo];

            if (['notaId', 'notaSerie', 'notaNumDoc', 'produtoId', 'qtdCom', 'vUnCom', 'vProd'].includes(campo)) {
                return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
            }

            if (campo === 'notaDataLanc') {
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            }

            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
                sensitivity: 'base',
            }) * multiplicador;
        });
    }, [agruparPorProduto, itens, ordenacao]);

    const alterarOrdenacao = (campo) => {
        setOrdenacao((atual) => ({
            campo,
            direcao: atual.campo === campo
                ? (atual.direcao === 'asc' ? 'desc' : 'asc')
                : 'asc',
        }));
    };

    const renderOrdenadorColuna = (campo, titulo, style = {}) => {
        const ativa = ordenacao.campo === campo;

        return (
            <button
                type="button"
                onClick={() => alterarOrdenacao(campo)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 'bold',
                    color: 'inherit',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: style.textAlign || 'left',
                }}
            >
                {titulo}
                {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
            </button>
        );
    };

    const calcularQuantidadeTotal = () => (
        itensExibidos.reduce((acc, item) => acc + Number(item.qtdCom || 0), 0)
    );

    const calcularValorTotal = () => (
        itensExibidos.reduce((acc, item) => acc + Number(item.vProd || 0), 0)
    );

    return (
        <PageContainer>
            <h2 className="page-title">Pesquisar Itens das Notas</h2>
            <PageHeader>
                <div className="group-div">
                    <InputCheck
                        id="agruparPorProduto"
                        label="Agrupar por Produto"
                        checked={agruparPorProduto}
                        onChange={(e) => setAgruparPorProduto(e.target.checked)}
                    />
                    <Input
                        label="Data Inicial"
                        onChange={(e) => setPeriodoInicial(e.target.value)}
                        type="date"
                        size={140}
                        value={periodoInicial}
                    />
                    <Input
                        label="Hora Início"
                        onChange={(e) => setHoraInicial(e.target.value)}
                        type="time"
                        size={90}
                        value={horaInicial}
                    />
                    <Input
                        label="Data Final"
                        onChange={(e) => setPeriodoFinal(e.target.value)}
                        type="date"
                        size={140}
                        value={periodoFinal}
                    />
                    <Input
                        label="Hora Fim"
                        onChange={(e) => setHoraFinal(e.target.value)}
                        type="time"
                        size={90}
                        value={horaFinal}
                    />
                    <Input
                        label="Produto"
                        value={produtoDescricao}
                        onChange={(e) => setProdutoDescricao(e.target.value)}
                        placeholder="Descrição do produto"
                        size={200}
                    />
                    <Input
                        label="Nome do Cliente"
                        placeholder="Digite o nome do cliente"
                        onChange={(e) => setCliente(e.target.value)}
                        value={cliente}
                        btnBuscar={handleBuscarCliente}
                        size={220}
                    />
                    <Input
                        label="Nº Doc."
                        value={numDoc}
                        onChange={(e) => setNumDoc(e.target.value)}
                    />                    
                    <Select
                        label="Situação NF"
                        value={situacao}
                        onChange={(e) => setSituacao(e.target.value)}
                        options={[
                            { value: -1, label: 'Autorizada/Recibo' },
                            { value: '', label: 'Todas' },                            
                            { value: 2, label: 'Autorizada' },
                            { value: 9, label: 'Recibo' },
                            { value: 10, label: 'Pendente' },
                            { value: 4, label: 'Cancelada' },
                            { value: 3, label: 'Rejeitada' },
                        ]}
                        size={170}
                    />
                </div>
                <button
                    style={{ marginRight: '5px' }}
                    type="button"
                    className="btn btn-search"
                    onClick={buscarItens}
                    disabled={disableBtn}
                >
                    Buscar
                </button>
            </PageHeader>

            <PageContent>
                <table className="page-content-table">
                    <thead>
                        <tr>
                            {!agruparPorProduto && (
                                <th style={{ textAlign: 'right' }}>
                                    {renderOrdenadorColuna('notaId', 'Id NF', { textAlign: 'right' })}
                                </th>
                            )}
                            {!agruparPorProduto && (
                                <th style={{ textAlign: 'right' }}>
                                    {renderOrdenadorColuna('notaSerie', 'Serie', { textAlign: 'right' })}
                                </th>
                            )}
                            {!agruparPorProduto && (
                                <th style={{ textAlign: 'right' }}>
                                    {renderOrdenadorColuna('notaNumDoc', 'Num. Doc', { textAlign: 'right' })}
                                </th>
                            )}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('tipoMovDescricao', 'Tipo Mov.')}</th>}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('caixaDescricao', 'Caixa')}</th>}
                            {!agruparPorProduto && (
                                <th style={{ textAlign: 'center' }}>
                                    {renderOrdenadorColuna('notaDataLanc', 'Data', { textAlign: 'center' })}
                                </th>
                            )}
                            {!agruparPorProduto && (
                                <th style={{ textAlign: 'center' }}>
                                    {renderOrdenadorColuna('hora', 'Hora', { textAlign: 'center' })}
                                </th>
                            )}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('clienteDescricao', 'Cliente')}</th>}
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('produtoId', 'Produto', { textAlign: 'right' })}
                            </th>
                            <th>{renderOrdenadorColuna('produtoDescricao', 'Descrição')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('qtdCom', 'Quantidade', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('vUnCom', 'Valor Unit.', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('vProd', 'Valor Total', { textAlign: 'right' })}
                            </th>
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('situacaoDescricao', 'Sit. NF')}</th>}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('notaModeloNF', 'Modelo')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {itensExibidos.map((item) => (
                            <tr key={agruparPorProduto ? `produto-${item.produtoId}` : item.id}>
                                {!agruparPorProduto && (
                                    <td style={{ textAlign: 'right', minWidth: '70px' }}>{item.notaId}</td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ textAlign: 'right', minWidth: '40px' }}>{item.notaSerie}</td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ textAlign: 'right', minWidth: '90px' }}>{item.notaNumDoc}</td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ minWidth: '100px' }}>{getDescricaoTipoMov(item.notaTipoMov)}</td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ minWidth: '90px' }}>{item.caixaDescricao}</td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ textAlign: 'center', minWidth: '100px' }}>
                                        {formatDate(item.notaDataLanc)}
                                    </td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ textAlign: 'center', minWidth: '60px' }}>
                                        {formatTime(item.notaDataLanc)}
                                    </td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ minWidth: '260px' }}>
                                        {item.notaClienteId} - {item.notaClienteNome}
                                    </td>
                                )}
                                <td style={{ textAlign: 'right', minWidth: '70px' }}>{item.produtoId}</td>
                                <td style={{ minWidth: '250px' }}>{item.xProd}</td>
                                <td style={{ textAlign: 'right', minWidth: '90px' }}>
                                    {formatNumeric(item.qtdCom)}
                                </td>
                                <td style={{ textAlign: 'right', minWidth: '100px' }}>
                                    {formatCurrency(item.vUnCom)}
                                </td>
                                <td style={{ textAlign: 'right', minWidth: '110px' }}>
                                    {formatCurrency(item.vProd)}
                                </td>
                                {!agruparPorProduto && (
                                    <td style={{ minWidth: '100px' }}>
                                        {getDescricaoSituacaoNF(item.notaSituacaoNF)}
                                    </td>
                                )}
                                {!agruparPorProduto && (
                                    <td style={{ minWidth: '70px' }}>{item.notaModeloNF}</td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </PageContent>

            <PageFooter>
                <div className="botoes-footer">
                    <strong>Itens: </strong> {itensExibidos.length}
                </div>
                <div className="totais-footer">
                    <strong>Qtd. Total: </strong> {formatNumeric(calcularQuantidadeTotal())}
                    {' | '}
                    <strong>Valor Total: </strong> {formatCurrency(calcularValorTotal())}
                </div>
            </PageFooter>
        </PageContainer>
    );
};

export default PesqNotasItemPage;
